import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { storageHelpers } from '@utils'
import { pubSubEventHandler, ErrorTrackingAPI } from '@services'
import { PUBSUB_GROUP_EVENT, ROUTES } from '@common'
import { logger } from '@utils'

import { useCartRedux } from './useRedux'
import { useCashRegister } from './useCashRegister'

export const usePaymentEvents = () => {
    const navigate = useNavigate()
    const { assistantCarts, clearAssistantData } = useCartRedux()
    const { joinedGroupName } = useCashRegister()

    // Payment event state
    const [paymentEvent, setPaymentEvent] = useState({
        isVisible: false,
        eventType: null,
        eventData: {},
    })

    // Track if we've already processed payment status on mount
    const hasProcessedPaymentStatusRef = useRef(false)

    // clear payment event data when new cart item is added (for closing modal)
    // But don't clear if we're currently showing a payment event modal
    useEffect(() => {
        if (assistantCarts) {
            if (assistantCarts[joinedGroupName]?.items?.length > 0) {
                // Clear payment event data when new cart item is added
                setPaymentEvent({
                    isVisible: false,
                    eventType: null,
                    eventData: {},
                })
            }
        }
    }, [assistantCarts, joinedGroupName])

    // Handle payment events
    const handlePaymentEvent = useCallback((eventType, eventData) => {
        // Prevent cancel event from overriding failed event if failed is already showing
        // This ensures users see the correct error message
        setPaymentEvent(prev => {
            // If failed modal is already visible, don't let cancel override it
            if (
                prev.isVisible &&
                prev.eventType === PUBSUB_GROUP_EVENT.PAYMENT_FAILED &&
                eventType === PUBSUB_GROUP_EVENT.PAYMENT_CANCEL
            ) {
                return prev
            }

            // Otherwise, update with new event
            return {
                isVisible: true,
                eventType,
                eventData,
            }
        })
    }, [])

    const handlePaymentEventClose = useCallback(() => {
        setPaymentEvent({
            isVisible: false,
            eventType: null,
            eventData: {},
        })
        clearAssistantData()
    }, [clearAssistantData])

    const handlePaymentFailed = useCallback(() => {
        setPaymentEvent({
            isVisible: true,
            eventType: PUBSUB_GROUP_EVENT.PAYMENT_FAILED,
            eventData: {},
        })
    }, [])

    const handlePaymentRequest = useCallback(
        payload => {
            handlePaymentEventClose()
            if (payload?.meta?.paymentUrl) {
                // Use localStorage for better persistence across redirects.
                storageHelpers.setPaymentOrigin('checkout-assistant')
                storageHelpers.setCheckoutAssistantPaymentSuccess({
                    number: payload.number,
                    timestamp: Date.now(),
                })

                // Extract qrCodeSVG and hostedVoucherUrl from payload if available
                const qrCodeSVG = payload?.meta?.qrCodeSVG
                const hostedVoucherUrl = payload?.meta?.hostedVoucherUrl

                if (!qrCodeSVG) {
                    ErrorTrackingAPI.sendError(
                        'QR code SVG not found in payment payload',
                        'usePaymentEvents',
                        'handlePaymentRequest',
                        JSON.stringify(payload?.meta)
                    )
                }

                // Parse the paymentUrl to extract parameters
                // Expected format: /order/:orderId/payment/:paymentIntentId/:clientSecret
                const url = new URL(payload.meta.paymentUrl, window.location.origin)
                const pathParts = url.pathname.split('/')

                // Extract parameters from URL path
                const orderIdIndex = pathParts.indexOf('order') + 1
                const paymentIndex = pathParts.indexOf('payment') + 1

                if (orderIdIndex > 0 && paymentIndex > 0 && pathParts.length >= paymentIndex + 2) {
                    const orderId = pathParts[orderIdIndex]
                    const paymentIntentId = pathParts[paymentIndex]
                    const clientSecret = pathParts[paymentIndex + 1]

                    navigate(
                        ROUTES.Payment.root
                            .replace(':orderId', orderId)
                            .replace(':paymentIntentId', paymentIntentId)
                            .replace(':clientSecret', clientSecret),
                        { state: { qrCodeSVG, hostedVoucherUrl } }
                    )
                } else {
                    // Fallback to original URL if parsing fails
                    logger.warn('Failed to parse paymentUrl, using original URL:', payload.meta.paymentUrl)
                    window.location.href = payload.meta.paymentUrl
                }
            } else {
                ErrorTrackingAPI.sendError(
                    'Payment URL not found in payload',
                    'usePaymentEvents',
                    'handlePaymentEvent',
                    JSON.stringify(payload)
                )
            }
        },
        [handlePaymentEventClose, navigate]
    )

    // Listen for payment events from PubSub
    useEffect(() => {
        const removePaymentLaterListener = pubSubEventHandler.addEventListener(PUBSUB_GROUP_EVENT.PAYMENT_LATER, payload => {
            handlePaymentEvent(PUBSUB_GROUP_EVENT.PAYMENT_LATER, payload)
        })

        const removePaymentRequestListener = pubSubEventHandler.addEventListener(PUBSUB_GROUP_EVENT.PAYMENT_REQUEST, payload => {
            handlePaymentEvent(PUBSUB_GROUP_EVENT.PAYMENT_REQUEST, payload)
        })

        const removePaymentSuccessListener = pubSubEventHandler.addEventListener(PUBSUB_GROUP_EVENT.PAYMENT_SUCCESS, payload => {
            handlePaymentEvent(PUBSUB_GROUP_EVENT.PAYMENT_SUCCESS, payload)
        })

        const removePaymentFailedListener = pubSubEventHandler.addEventListener(PUBSUB_GROUP_EVENT.PAYMENT_FAILED, payload => {
            handlePaymentEvent(PUBSUB_GROUP_EVENT.PAYMENT_FAILED, payload)
        })

        const removePaymentCancelListener = pubSubEventHandler.addEventListener(PUBSUB_GROUP_EVENT.PAYMENT_CANCEL, payload => {
            handlePaymentEvent(PUBSUB_GROUP_EVENT.PAYMENT_CANCEL, payload)
        })

        const removePaymentPaywaveSuccessListener = pubSubEventHandler.addEventListener(
            PUBSUB_GROUP_EVENT.PAYMENT_PAYWAVE_SUCCESS,
            payload => {
                handlePaymentEvent(PUBSUB_GROUP_EVENT.PAYMENT_PAYWAVE_SUCCESS, payload)
            }
        )

        return () => {
            removePaymentLaterListener()
            removePaymentRequestListener()
            removePaymentSuccessListener()
            removePaymentFailedListener()
            removePaymentCancelListener()
            removePaymentPaywaveSuccessListener()
        }
    }, [handlePaymentEvent])

    // Clear assistant data when returning from payment - only run once on mount
    useEffect(() => {
        // Prevent duplicate processing if already handled
        if (hasProcessedPaymentStatusRef.current) {
            return
        }

        // Read all flags FIRST before any clearing operations
        const paymentOrigin = storageHelpers.getPaymentOrigin()
        const checkoutAssistantPaymentSuccess = storageHelpers.getCheckoutAssistantPaymentSuccess()
        const checkoutAssistantPaymentCancel = storageHelpers.getCheckoutAssistantPaymentCancel()

        // Mark as processed to prevent duplicate handling
        hasProcessedPaymentStatusRef.current = true

        // Only clear assistant data if we're returning from payment screen
        // This prevents clearing data when the page is just loaded normally
        if (paymentOrigin === 'checkout-assistant') {
            clearAssistantData()
        }

        // Helper function to set payment event with DOM readiness check
        // This prevents Modal from trying to render when DOM is not ready during navigation
        const setPaymentEventSafely = (eventType, eventData) => {
            // Wait for DOM to be ready before showing modal
            const waitForDOM = () => {
                return new Promise(resolve => {
                    if (typeof window !== 'undefined' && document.body) {
                        resolve()
                        return
                    }

                    // Wait for body to appear
                    let attempts = 0
                    const maxAttempts = 100 // 5 seconds max
                    const checkInterval = setInterval(() => {
                        attempts++
                        if (document.body) {
                            clearInterval(checkInterval)
                            resolve()
                        } else if (attempts >= maxAttempts) {
                            clearInterval(checkInterval)
                            resolve() // Resolve anyway to prevent infinite waiting
                        }
                    }, 50)
                })
            }

            // Wait for DOM, then set payment event
            waitForDOM().then(() => {
                // Use setTimeout to ensure this happens after React has finished rendering
                setTimeout(() => {
                    setPaymentEvent({
                        isVisible: true,
                        eventType,
                        eventData,
                    })
                }, 0)
            })
        }

        // Check payment status flags - priority: success > cancel > failed
        if (checkoutAssistantPaymentSuccess) {
            try {
                const paymentData = checkoutAssistantPaymentSuccess
                // Remove the flag immediately to prevent showing again
                storageHelpers.clearCheckoutAssistantPaymentSuccess()

                // Show payment success popup with order ID
                setPaymentEventSafely(PUBSUB_GROUP_EVENT.PAYMENT_SUCCESS, paymentData)
            } catch (error) {
                ErrorTrackingAPI.sendError(error, 'usePaymentEvents', 'parsePaymentSuccess')
                storageHelpers.clearCheckoutAssistantPaymentSuccess()
            }
        } else if (checkoutAssistantPaymentCancel) {
            // Check cancel flag - if it exists, show cancel modal
            // Note: We check this even if paymentOrigin is not set, in case it was cleared
            try {
                // Remove the flag immediately to prevent showing again
                storageHelpers.clearCheckoutAssistantPaymentCancel()

                // Show payment cancel popup with DOM readiness check
                setPaymentEventSafely(PUBSUB_GROUP_EVENT.PAYMENT_CANCEL, {})
            } catch (error) {
                ErrorTrackingAPI.sendError(error, 'usePaymentEvents', 'parsePaymentCancel')
                storageHelpers.clearCheckoutAssistantPaymentCancel()
            }
        } else {
            // If not returning from payment screen, only clear if event is not already visible
            // This prevents clearing the modal if it was set in a previous mount
            setPaymentEvent(prev => {
                if (prev.isVisible) {
                    // Keep the modal visible if it's already showing
                    return prev
                }
                // Otherwise clear it
                return {
                    isVisible: false,
                    eventType: null,
                    eventData: {},
                }
            })
        }

        // Always clear payment origin flag after processing to prevent stale state
        // This ensures the flag is cleared even if no success data exists
        if (paymentOrigin === 'checkout-assistant') {
            storageHelpers.clearPaymentOrigin()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return {
        paymentEvent,
        handlePaymentEventClose,
        handlePaymentRequest,
        handlePaymentFailed,
    }
}
